import React, { useEffect, useMemo, useState } from "react";
import { Spin } from "antd";
import { get, isEmpty } from "lodash";
import {
  getBatchReports,
  reviseReport,
  reviseTimeSeriesData,
} from "services/gaService";
import { useReportContext } from "components/ReportContext/ReportContextProvider";
import { useUserEngagementAnalyticsContext } from "./UserEngagementAnalyticsContextProvider";
import UserEngagementAnalyticsOverview from "./components/UserEngagementAnalyticsOverview";
import { alertUnknownError } from "services/notificationService";
import {
  DATAKEY_USER_ACTIVATION,
  DATAKEY_MERKEN_IMPRESSIONS,
  DATAKEY_MERKEN_CLICKS_BY_VARIANT,
  DATAKEY_MERKEN_CTR,
  DATAKEY_PLUGIT_DOCK,
  DATAKEY_TOOLTIP,
  DATAKEY_WIZARD,
  DATAKEY_ONBOARDING_TOOLTIP_WIZARD,
  DATAKEY_TOOLTIP_OPEN,
  DATAKEY_TOTAL_ACTIVATION,
  DATAKEY_CONVERSION_RATE,
  DATAKEY_USER_SIGNUP,
  DATAKEY_SIGNUP_RATE,
  CATEGORY_DATA_KEYS,
  REPORT_INDEX_ACTIVATION,
  REPORT_INDEX_USER_SIGNUP,
  REPORT_INDEX_MERKEN_CLICKS,
  REPORT_INDEX_MERKEN_IMPRESSIONS,
  REPORT_INDEX_MERKEN_CLICKS_BY_VARIANT,
  REPORT_INDEX_TOOLTIP_OPEN,
  REPORT_INDEX_WIZARD_STEP_IMPRESSIONS,
  REPORT_INDEX_WIZARD_DISMISSALS,
  REPORT_INDEX_WIZARD_REGISTER_CLICKS,
} from "./constants";
import { DIMENSION_DATE } from "constants/dimensions";
import { METRIC_EVENT_COUNT } from "constants/metrics";
import {
  DIMENSION_ITEM_CATEGORY2,
  DIMENSION_ITEM_SOURCE_STREAM_CATEGORY,
  DIMENSION_PLATFORM,
  DIMENSION_STEP_NUMBER,
} from "constants/customDimensions";
import { useParams } from "react-router-dom";

const normalizeCategory = (category) => {
  if (!category) return null;
  const lower = category.toLowerCase();
  if (lower.includes("plugit") || lower.includes("dock")) {
    return DATAKEY_PLUGIT_DOCK;
  }
  if (lower.includes("tooltip")) {
    return DATAKEY_TOOLTIP;
  }
  if (lower.includes("wizard")) {
    return DATAKEY_WIZARD;
  }
  return null;
};

const groupByDateAndCategory = (rows = []) => {
  return rows.reduce((result, current) => {
    const date = current[DIMENSION_DATE];
    const category = normalizeCategory(current[DIMENSION_ITEM_CATEGORY2]);
    const count = current[METRIC_EVENT_COUNT] || 0;

    if (!result[date]) {
      result[date] = {
        [DATAKEY_PLUGIT_DOCK]: 0,
        [DATAKEY_TOOLTIP]: 0,
        [DATAKEY_WIZARD]: 0,
      };
    }

    if (category === DATAKEY_PLUGIT_DOCK) {
      result[date][DATAKEY_PLUGIT_DOCK] += count;
    } else if (category === DATAKEY_TOOLTIP) {
      result[date][DATAKEY_TOOLTIP] += count;
    } else if (category === DATAKEY_WIZARD) {
      result[date][DATAKEY_WIZARD] += count;
    }

    return result;
  }, {});
};

const transformToTimeSeriesData = (groupedData, totalDataKey) => {
  const allDates = Object.keys(groupedData);
  const uniqueDates = [...new Set(allDates)];

  return uniqueDates.map((date) => {
    const plugitDock = groupedData[date][DATAKEY_PLUGIT_DOCK] || 0;
    const tooltip = groupedData[date][DATAKEY_TOOLTIP] || 0;
    const wizard = groupedData[date][DATAKEY_WIZARD] || 0;
    const firstTimeTooltip = tooltip + wizard;
    const total = plugitDock + tooltip + wizard;

    // calculate percentages
    const plugitDockPercent = total > 0 ? (plugitDock / total) * 100 : 0;
    const tooltipPercent = total > 0 ? (tooltip / total) * 100 : 0;
    const wizardPercent = total > 0 ? (wizard / total) * 100 : 0;

    return {
      [DIMENSION_DATE]: date,
      [totalDataKey]: total,
      [DATAKEY_PLUGIT_DOCK]: plugitDock,
      [DATAKEY_TOOLTIP]: tooltip,
      [DATAKEY_WIZARD]: wizard,
      [DATAKEY_ONBOARDING_TOOLTIP_WIZARD]: firstTimeTooltip,
      [`${DATAKEY_PLUGIT_DOCK}Percent`]: plugitDockPercent,
      [`${DATAKEY_TOOLTIP}Percent`]: tooltipPercent,
      [`${DATAKEY_WIZARD}Percent`]: wizardPercent,
    };
  });
};

const processReportData = (report, totalDataKey, dateStrings) => {
  if (!report || !report.rows || report.rows.length === 0) {
    return [];
  }

  const groupedData = groupByDateAndCategory(report.rows);
  const combinedData = transformToTimeSeriesData(groupedData, totalDataKey);

  // fill in missing dates with zeros
  const series = reviseTimeSeriesData(combinedData, {
    dateKey: DIMENSION_DATE,
    startDate: dateStrings[0],
    endDate: dateStrings[1],
    dataKeys: [
      totalDataKey,
      ...CATEGORY_DATA_KEYS,
      DATAKEY_ONBOARDING_TOOLTIP_WIZARD,
    ],
  });

  return series;
};

const groupByDateAndVariant = (rows = []) => {
  return rows.reduce((result, current) => {
    const date = current[DIMENSION_DATE];
    const variantId = current[DIMENSION_ITEM_CATEGORY2] || "";
    const count = current[METRIC_EVENT_COUNT] || 0;

    if (!result[date]) {
      result[date] = {};
    }

    if (!result[date][variantId]) {
      result[date][variantId] = 0;
    }

    result[date][variantId] += count;

    return result;
  }, {});
};

const transformVariantToTimeSeriesData = (groupedData, totalDataKey) => {
  const allDates = Object.keys(groupedData);
  const uniqueDates = [...new Set(allDates)];

  // collect all variant IDs
  const allVariantIds = new Set();
  uniqueDates.forEach((date) => {
    Object.keys(groupedData[date]).forEach((variantId) => {
      if (variantId) {
        allVariantIds.add(variantId);
      }
    });
  });

  return uniqueDates.map((date) => {
    const dateData = groupedData[date] || {};
    let total = 0;
    const result = {
      [DIMENSION_DATE]: date,
    };

    // add each variant ID as a data key
    allVariantIds.forEach((variantId) => {
      const count = dateData[variantId] || 0;
      result[variantId] = count;
      total += count;
    });

    result[totalDataKey] = total;
    return result;
  });
};

const processVariantReportData = (report, totalDataKey, dateStrings) => {
  if (!report || !report.rows || report.rows.length === 0) {
    return { data: [], variantIds: [] };
  }

  const groupedData = groupByDateAndVariant(report.rows);
  const combinedData = transformVariantToTimeSeriesData(
    groupedData,
    totalDataKey
  );

  // collect all variant IDs for chart lines
  const variantIds = new Set();
  combinedData.forEach((item) => {
    Object.keys(item).forEach((key) => {
      if (key !== DIMENSION_DATE && key !== totalDataKey) {
        variantIds.add(key);
      }
    });
  });
  const variantIdsArray = Array.from(variantIds).sort();

  // fill in missing dates with zeros
  const series = reviseTimeSeriesData(combinedData, {
    dateKey: DIMENSION_DATE,
    startDate: dateStrings[0],
    endDate: dateStrings[1],
    dataKeys: [totalDataKey, ...variantIdsArray],
  });

  return { data: series, variantIds: variantIdsArray };
};

// Toggle to true to test wizard drop-off UI without GA data
const USE_MOCK_WIZARD_DATA = false;

const MOCK_WIZARD_STEP_REPORT = {
  rows: [
    {
      [DIMENSION_DATE]: "20260501",
      [DIMENSION_STEP_NUMBER]: "1",
      [METRIC_EVENT_COUNT]: 200,
    },
    {
      [DIMENSION_DATE]: "20260501",
      [DIMENSION_STEP_NUMBER]: "2",
      [METRIC_EVENT_COUNT]: 180,
    },
    {
      [DIMENSION_DATE]: "20260501",
      [DIMENSION_STEP_NUMBER]: "3",
      [METRIC_EVENT_COUNT]: 150,
    },
    {
      [DIMENSION_DATE]: "20260501",
      [DIMENSION_STEP_NUMBER]: "4",
      [METRIC_EVENT_COUNT]: 120,
    },
    {
      [DIMENSION_DATE]: "20260501",
      [DIMENSION_STEP_NUMBER]: "5",
      [METRIC_EVENT_COUNT]: 90,
    },
    {
      [DIMENSION_DATE]: "20260501",
      [DIMENSION_STEP_NUMBER]: "6",
      [METRIC_EVENT_COUNT]: 70,
    },
  ],
};

const MOCK_WIZARD_DISMISS_REPORT = {
  rows: [{ [DIMENSION_DATE]: "20260501", [METRIC_EVENT_COUNT]: 130 }],
};

const UserEngagementAnalytics = () => {
  const { reportRequests } = useUserEngagementAnalyticsContext();
  const [reports, setReports] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const { dateStrings } = useReportContext();
  const { id: domain } = useParams();

  const resetData = () => {
    setReports([]);
  };

  useEffect(() => {
    if (!reportRequests) {
      return;
    }

    if (!Array.isArray(reportRequests) || reportRequests.length === 0) {
      return;
    }

    const BATCH_SIZE = 5;
    const loadData = async () => {
      setIsLoading(true);
      try {
        const chunks = [];
        for (let i = 0; i < reportRequests.length; i += BATCH_SIZE) {
          chunks.push(reportRequests.slice(i, i + BATCH_SIZE));
        }
        const batchResults = await Promise.all(
          chunks.map((chunk) => getBatchReports(chunk, domain))
        );
        const reports = batchResults.flatMap((data) =>
          get(data, "reports", [])
        );
        // reports are now in the correct order matching report index constants:
        // [0: user activation, 1: user signup, 2: tooltip open, 3: merken clicks, 4: merken impressions, 5: merken clicks by variant]
        setReports(reports);
      } catch (error) {
        alertUnknownError();
        resetData();
      } finally {
        setIsLoading(false);
      }
    };

    loadData();
  }, [reportRequests, domain]);

  const userActivationReport = useMemo(() => {
    return reviseReport(reports[REPORT_INDEX_ACTIVATION]);
  }, [reports]);

  const userSignupReport = useMemo(() => {
    return reviseReport(reports[REPORT_INDEX_USER_SIGNUP]);
  }, [reports]);

  const merkenClicksReport = useMemo(() => {
    return reviseReport(reports[REPORT_INDEX_MERKEN_CLICKS]);
  }, [reports]);

  const merkenImpressionsReport = useMemo(() => {
    return reviseReport(reports[REPORT_INDEX_MERKEN_IMPRESSIONS]);
  }, [reports]);

  const merkenClicksByVariantReport = useMemo(() => {
    return reviseReport(reports[REPORT_INDEX_MERKEN_CLICKS_BY_VARIANT]);
  }, [reports]);

  const tooltipOpenReport = useMemo(() => {
    return reviseReport(reports[REPORT_INDEX_TOOLTIP_OPEN]);
  }, [reports]);

  const wizardStepReport = useMemo(() => {
    if (USE_MOCK_WIZARD_DATA) return MOCK_WIZARD_STEP_REPORT;
    return reviseReport(reports[REPORT_INDEX_WIZARD_STEP_IMPRESSIONS]);
  }, [reports]);

  const wizardDismissReport = useMemo(() => {
    if (USE_MOCK_WIZARD_DATA) return MOCK_WIZARD_DISMISS_REPORT;
    return reviseReport(reports[REPORT_INDEX_WIZARD_DISMISSALS]);
  }, [reports]);

  const wizardRegisterReport = useMemo(() => {
    if (USE_MOCK_WIZARD_DATA) return { rows: [{ [METRIC_EVENT_COUNT]: 60 }] };
    return reviseReport(reports[REPORT_INDEX_WIZARD_REGISTER_CLICKS]);
  }, [reports]);

  // process time series data for user activation
  const userActivationData = useMemo(() => {
    return processReportData(
      userActivationReport,
      DATAKEY_USER_ACTIVATION,
      dateStrings
    );
  }, [userActivationReport, dateStrings]);

  // tooltip open by date (single dimension date)
  const tooltipOpenByDate = useMemo(() => {
    if (!tooltipOpenReport?.rows?.length) return {};
    return tooltipOpenReport.rows.reduce((acc, row) => {
      const date = row[DIMENSION_DATE];
      acc[date] = (acc[date] || 0) + (Number(row[METRIC_EVENT_COUNT]) || 0);
      return acc;
    }, {});
  }, [tooltipOpenReport]);

  // total user signup by date (report 1: USER_REGISTRATION)
  // only Tooltip and Wizard; exclude Plugit Dock
  const userSignupData = useMemo(() => {
    const allowedCategories = [DATAKEY_TOOLTIP, DATAKEY_WIZARD];
    if (!userSignupReport?.rows?.length) {
      return reviseTimeSeriesData([], {
        dateKey: DIMENSION_DATE,
        startDate: dateStrings[0],
        endDate: dateStrings[1],
        dataKeys: [DATAKEY_USER_SIGNUP],
      }).map((item) => ({ ...item, [DATAKEY_USER_SIGNUP]: 0 }));
    }

    const byDate = userSignupReport.rows.reduce((acc, row) => {
      const category = normalizeCategory(row[DIMENSION_ITEM_CATEGORY2]);
      if (!allowedCategories.includes(category)) return acc;
      const date = row[DIMENSION_DATE];
      acc[date] = (acc[date] || 0) + (Number(row[METRIC_EVENT_COUNT]) || 0);
      return acc;
    }, {});

    const combinedData = Object.entries(byDate)
      .sort(([a], [b]) => a.localeCompare(b))
      .map(([date, count]) => ({
        [DIMENSION_DATE]: date,
        [DATAKEY_USER_SIGNUP]: count,
      }));

    return reviseTimeSeriesData(combinedData, {
      dateKey: DIMENSION_DATE,
      startDate: dateStrings[0],
      endDate: dateStrings[1],
      dataKeys: [DATAKEY_USER_SIGNUP],
    });
  }, [userSignupReport, dateStrings]);

  // conversion rate: (tooltip + wizard) / tooltip open; includes signup and signup/tooltip rate
  const conversionRateData = useMemo(() => {
    const totalActivationByDate = (userActivationData || []).reduce(
      (acc, item) => {
        const date = item[DIMENSION_DATE];
        const tooltip = Number(item[DATAKEY_TOOLTIP]) || 0;
        const wizard = Number(item[DATAKEY_WIZARD]) || 0;
        const totalActivation = tooltip + wizard;
        acc[date] = totalActivation;
        return acc;
      },
      {}
    );

    const signupByDate = (userSignupData || []).reduce((acc, item) => {
      const date = item[DIMENSION_DATE];
      acc[date] = Number(item[DATAKEY_USER_SIGNUP]) || 0;
      return acc;
    }, {});

    const allDates = new Set([
      ...Object.keys(totalActivationByDate),
      ...Object.keys(tooltipOpenByDate),
      ...Object.keys(signupByDate),
    ]);

    const combinedData = [...allDates].sort().map((date) => {
      const totalActivation = totalActivationByDate[date] || 0;
      const tooltipOpen = tooltipOpenByDate[date] || 0;
      const userSignup = signupByDate[date] || 0;
      const conversionRate =
        tooltipOpen > 0
          ? parseFloat(((totalActivation / tooltipOpen) * 100).toFixed(1))
          : 0;
      const signupRate =
        tooltipOpen > 0
          ? parseFloat(((userSignup / tooltipOpen) * 100).toFixed(1))
          : 0;
      return {
        [DIMENSION_DATE]: date,
        [DATAKEY_TOTAL_ACTIVATION]: totalActivation,
        [DATAKEY_TOOLTIP_OPEN]: tooltipOpen,
        [DATAKEY_USER_SIGNUP]: userSignup,
        [DATAKEY_CONVERSION_RATE]: conversionRate,
        [DATAKEY_SIGNUP_RATE]: signupRate,
      };
    });

    const dataKeys = [
      DATAKEY_TOTAL_ACTIVATION,
      DATAKEY_TOOLTIP_OPEN,
      DATAKEY_USER_SIGNUP,
      DATAKEY_CONVERSION_RATE,
      DATAKEY_SIGNUP_RATE,
    ];

    return reviseTimeSeriesData(combinedData, {
      dateKey: DIMENSION_DATE,
      startDate: dateStrings[0],
      endDate: dateStrings[1],
      dataKeys,
    }).map((item) => {
      const totalActivation = Number(item[DATAKEY_TOTAL_ACTIVATION]) || 0;
      const tooltipOpen = Number(item[DATAKEY_TOOLTIP_OPEN]) || 0;
      const userSignup = Number(item[DATAKEY_USER_SIGNUP]) || 0;
      const conversionRate =
        tooltipOpen > 0
          ? parseFloat(((totalActivation / tooltipOpen) * 100).toFixed(1))
          : 0;
      const signupRate =
        tooltipOpen > 0
          ? parseFloat(((userSignup / tooltipOpen) * 100).toFixed(1))
          : 0;
      return {
        ...item,
        [DATAKEY_CONVERSION_RATE]: conversionRate,
        [DATAKEY_SIGNUP_RATE]: signupRate,
      };
    });
  }, [userActivationData, tooltipOpenByDate, userSignupData, dateStrings]);

  // process time series data for merken button impressions (variant IDs)
  const merkenImpressionsResult = useMemo(() => {
    return processVariantReportData(
      merkenImpressionsReport,
      DATAKEY_MERKEN_IMPRESSIONS,
      dateStrings
    );
  }, [merkenImpressionsReport, dateStrings]);

  // process time series data for merken button clicks by variant ID
  const merkenClicksByVariantResult = useMemo(() => {
    return processVariantReportData(
      merkenClicksByVariantReport,
      DATAKEY_MERKEN_CLICKS_BY_VARIANT,
      dateStrings
    );
  }, [merkenClicksByVariantReport, dateStrings]);

  // process CTR by variant (clicks / impressions for each variant ID)
  const merkenCTRByVariantResult = useMemo(() => {
    const impressionsData = merkenImpressionsResult.data || [];
    const clicksData = merkenClicksByVariantResult.data || [];
    const impressionsVariantIds = merkenImpressionsResult.variantIds || [];
    const clicksVariantIds = merkenClicksByVariantResult.variantIds || [];

    if (impressionsData.length === 0 && clicksData.length === 0) {
      return { data: [], variantIds: [] };
    }

    // get all unique variant IDs from both datasets
    const allVariantIds = new Set([
      ...impressionsVariantIds,
      ...clicksVariantIds,
    ]);
    const variantIdsArray = Array.from(allVariantIds).sort();

    // create a map of dates from both datasets
    const allDates = new Set();
    impressionsData.forEach((item) => allDates.add(item[DIMENSION_DATE]));
    clicksData.forEach((item) => allDates.add(item[DIMENSION_DATE]));

    // combine data by date and calculate CTR for each variant
    const combinedData = Array.from(allDates).map((date) => {
      const impressionsItem = impressionsData.find(
        (item) => item[DIMENSION_DATE] === date
      );
      const clicksItem = clicksData.find(
        (item) => item[DIMENSION_DATE] === date
      );

      const result = {
        [DIMENSION_DATE]: date,
      };

      let totalImpressions = 0;
      let totalClicks = 0;

      // calculate CTR for each variant ID
      variantIdsArray.forEach((variantId) => {
        const impressions = impressionsItem?.[variantId] || 0;
        const clicks = clicksItem?.[variantId] || 0;
        const ctr =
          impressions > 0
            ? parseFloat(((clicks / impressions) * 100).toFixed(1))
            : 0;

        // store CTR with variant ID as key
        result[`${variantId}_CTR`] = ctr;

        // accumulate totals for overall CTR
        totalImpressions += impressions;
        totalClicks += clicks;
      });

      // calculate overall CTR (total clicks / total impressions)
      const overallCTR =
        totalImpressions > 0
          ? parseFloat(((totalClicks / totalImpressions) * 100).toFixed(1))
          : 0;

      result[DATAKEY_MERKEN_CTR] = overallCTR;

      return result;
    });

    // create data keys for CTR by variant and overall CTR
    const ctrDataKeys = [
      DATAKEY_MERKEN_CTR,
      ...variantIdsArray.map((id) => `${id}_CTR`),
    ];

    // fill in missing dates with zeros
    const series = reviseTimeSeriesData(combinedData, {
      dateKey: DIMENSION_DATE,
      startDate: dateStrings[0],
      endDate: dateStrings[1],
      dataKeys: ctrDataKeys,
    });

    // recalculate CTR for filled dates
    const finalData = series.map((item) => {
      const impressionsItem = impressionsData.find(
        (dataItem) => dataItem[DIMENSION_DATE] === item[DIMENSION_DATE]
      );
      const clicksItem = clicksData.find(
        (dataItem) => dataItem[DIMENSION_DATE] === item[DIMENSION_DATE]
      );

      const result = { ...item };

      let totalImpressions = 0;
      let totalClicks = 0;

      variantIdsArray.forEach((variantId) => {
        const impressions = impressionsItem?.[variantId] || 0;
        const clicks = clicksItem?.[variantId] || 0;
        const ctr =
          impressions > 0
            ? parseFloat(((clicks / impressions) * 100).toFixed(1))
            : 0;

        result[`${variantId}_CTR`] = ctr;

        // accumulate totals for overall CTR
        totalImpressions += impressions;
        totalClicks += clicks;
      });

      // recalculate overall CTR
      const overallCTR =
        totalImpressions > 0
          ? parseFloat(((totalClicks / totalImpressions) * 100).toFixed(1))
          : 0;

      result[DATAKEY_MERKEN_CTR] = overallCTR;

      return result;
    });

    return { data: finalData, variantIds: variantIdsArray };
  }, [
    merkenImpressionsResult.data,
    merkenImpressionsResult.variantIds,
    merkenClicksByVariantResult.data,
    merkenClicksByVariantResult.variantIds,
    dateStrings,
  ]);

  // process time series data for FTU (tooltip open and merken clicks)
  const ftuData = useMemo(() => {
    if (isEmpty(tooltipOpenReport) && isEmpty(merkenClicksReport)) return [];

    // group merken clicks by date
    const merkenClicksByDate = (merkenClicksReport.rows || []).reduce(
      (result, current) => {
        const date = current[DIMENSION_DATE];
        result[date] = current[METRIC_EVENT_COUNT] || 0;
        return result;
      },
      {}
    );

    // combine both datasets (tooltip open by date)
    const allDates = [
      ...Object.keys(tooltipOpenByDate),
      ...Object.keys(merkenClicksByDate),
    ];
    const uniqueDates = [...new Set(allDates)];

    const combinedData = uniqueDates.map((date) => {
      const tooltipOpen = tooltipOpenByDate[date] || 0;
      const merkenClicks = merkenClicksByDate[date] || 0;
      const clickRate =
        tooltipOpen > 0
          ? parseFloat(((merkenClicks / tooltipOpen) * 100).toFixed(1))
          : 0;

      return {
        [DIMENSION_DATE]: date,
        tooltipOpen,
        merkenClicks,
        clickRate,
      };
    });

    // fill in missing dates with zeros
    const series = reviseTimeSeriesData(combinedData, {
      dateKey: DIMENSION_DATE,
      startDate: dateStrings[0],
      endDate: dateStrings[1],
      dataKeys: ["tooltipOpen", "merkenClicks", "clickRate"],
    });

    // recalculate click rate for filled dates
    return series.map((item) => {
      const tooltipOpen = item.tooltipOpen || 0;
      const merkenClicks = item.merkenClicks || 0;
      const clickRate =
        tooltipOpen > 0
          ? parseFloat(((merkenClicks / tooltipOpen) * 100).toFixed(1))
          : 0;

      return {
        ...item,
        clickRate,
      };
    });
  }, [tooltipOpenByDate, tooltipOpenReport, merkenClicksReport, dateStrings]);

  // process table data from queries 3 and 4 (aggregated by variant ID)
  const merkenTableData = useMemo(() => {
    if (!merkenImpressionsReport?.rows || !merkenClicksByVariantReport?.rows) {
      return [];
    }

    // aggregate impressions by variant ID and store zoneType and platform
    const impressionsByVariant = (merkenImpressionsReport.rows || []).reduce(
      (result, row) => {
        const variantId = row[DIMENSION_ITEM_CATEGORY2] || "";
        const count = parseInt(row[METRIC_EVENT_COUNT] || 0, 10);
        result[variantId] = result[variantId] || {
          count: 0,
          zoneType: null,
          platform: null,
        };
        result[variantId].count += count;
        // store zoneType from first row (assuming consistent zoneType per variant)
        if (!result[variantId].zoneType) {
          result[variantId].zoneType =
            row[DIMENSION_ITEM_SOURCE_STREAM_CATEGORY] || null;
        }
        // store platform from first row (assuming consistent platform per variant)
        if (!result[variantId].platform) {
          result[variantId].platform = row[DIMENSION_PLATFORM] || null;
        }
        return result;
      },
      {}
    );

    // aggregate clicks by variant ID
    const clicksByVariant = (merkenClicksByVariantReport.rows || []).reduce(
      (result, row) => {
        const variantId = row[DIMENSION_ITEM_CATEGORY2] || "";
        const count = parseInt(row[METRIC_EVENT_COUNT] || 0, 10);
        result[variantId] = (result[variantId] || 0) + count;
        return result;
      },
      {}
    );

    // get all unique variant IDs
    const allVariantIds = new Set([
      ...Object.keys(impressionsByVariant),
      ...Object.keys(clicksByVariant),
    ]);

    // create table rows
    const tableData = Array.from(allVariantIds)
      .sort()
      .map((variantId) => {
        const impressions = impressionsByVariant[variantId]?.count || 0;
        const clicks = clicksByVariant[variantId] || 0;
        const ctr =
          impressions > 0
            ? parseFloat(((clicks / impressions) * 100).toFixed(1))
            : 0;

        // generate variant name from variant ID
        const variantName =
          variantId === "base"
            ? "Base Merken"
            : variantId.startsWith("v")
              ? `Variant ${variantId.substring(1)}`
              : `Variant ${variantId}`;

        // get zoneType from impressions data, fallback to clicks data if not found
        let zoneType = impressionsByVariant[variantId]?.zoneType;
        if (!zoneType) {
          // try to get from clicks report as fallback
          const clicksRow = (merkenClicksByVariantReport.rows || []).find(
            (row) => row[DIMENSION_ITEM_CATEGORY2] === variantId
          );
          zoneType = clicksRow?.[DIMENSION_ITEM_SOURCE_STREAM_CATEGORY] || null;
        }

        // get platform from impressions data, fallback to clicks data if not found
        let platform = impressionsByVariant[variantId]?.platform;
        if (!platform) {
          // try to get from clicks report as fallback
          const clicksRow = (merkenClicksByVariantReport.rows || []).find(
            (row) => row[DIMENSION_ITEM_CATEGORY2] === variantId
          );
          platform = clicksRow?.[DIMENSION_PLATFORM] || null;
        }

        return {
          variantId,
          variantName,
          website: domain || "X",
          platform: platform || "",
          zoneType: zoneType || "",
          impressions,
          clicks,
          ctr,
        };
      });

    return tableData;
  }, [merkenImpressionsReport, merkenClicksByVariantReport, domain]);

  // aggregate wizard step impressions by step number across the entire date range
  const wizardStepData = useMemo(() => {
    if (!wizardStepReport?.rows?.length) return [];

    const stepCounts = wizardStepReport.rows.reduce((acc, row) => {
      const stepRaw = row[DIMENSION_STEP_NUMBER];
      const count = Number(row[METRIC_EVENT_COUNT]) || 0;
      if (!stepRaw) return acc;
      const step = parseInt(stepRaw, 10);
      if (isNaN(step)) return acc;
      acc[step] = (acc[step] || 0) + count;
      return acc;
    }, {});

    const sortedSteps = Object.keys(stepCounts)
      .map(Number)
      .sort((a, b) => a - b);

    return sortedSteps.map((step, index) => {
      const count = stepCounts[step];
      const nextStep = sortedSteps[index + 1];
      const nextCount = nextStep != null ? stepCounts[nextStep] : null;
      const dropoffRate =
        nextCount != null && count > 0
          ? parseFloat((((count - nextCount) / count) * 100).toFixed(1))
          : null;
      return { step, count, dropoffRate };
    });
  }, [wizardStepReport]);

  // wizard impressions = step 1 count; dismiss rate = (impressions - registerClicks) / impressions
  const wizardSummary = useMemo(() => {
    const impressions = wizardStepData.length > 0 ? wizardStepData[0].count : 0;

    const dismissals = (wizardDismissReport?.rows || []).reduce((acc, row) => {
      return acc + (Number(row[METRIC_EVENT_COUNT]) || 0);
    }, 0);

    const registerClicks = (wizardRegisterReport?.rows || []).reduce(
      (acc, row) => acc + (Number(row[METRIC_EVENT_COUNT]) || 0),
      0
    );

    const dismissRate =
      impressions > 0
        ? parseFloat(((dismissals / impressions) * 100).toFixed(1))
        : 0;

    return { impressions, dismissals, registerClicks, dismissRate };
  }, [wizardStepData, wizardDismissReport, wizardRegisterReport]);

  return (
    <Spin spinning={isLoading}>
      <UserEngagementAnalyticsOverview
        userActivationData={userActivationData}
        conversionRateData={conversionRateData}
        merkenImpressionsData={merkenImpressionsResult.data}
        merkenImpressionsVariantIds={merkenImpressionsResult.variantIds}
        merkenClicksByVariantData={merkenClicksByVariantResult.data}
        merkenClicksByVariantVariantIds={merkenClicksByVariantResult.variantIds}
        merkenCTRByVariantData={merkenCTRByVariantResult.data}
        merkenCTRByVariantVariantIds={merkenCTRByVariantResult.variantIds}
        merkenTableData={merkenTableData}
        ftuData={ftuData}
        wizardStepData={wizardStepData}
        wizardImpressions={wizardSummary.impressions}
        wizardDismissals={wizardSummary.dismissals}
        wizardRegisterClicks={wizardSummary.registerClicks}
        wizardDismissRate={wizardSummary.dismissRate}
      />
    </Spin>
  );
};

export default UserEngagementAnalytics;
